import React, { useState, useMemo } from 'react'
import { Box, Alert, LinearProgress } from '@mui/material'
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined'
import PageTitle from '../../../components/PageTitle'
import { KpiRow, Pill, SectionCard, ActionIconBtn } from '../components/common'
import DataTable from '../components/Tables'
import ProposalDetailModal from '../components/ProposalDetailModal'
import { useListProposalsQuery } from '../../../redux/d2cApiSlice'
import { proposalRow, inGroup, computeKpis } from '../apiMap'

export default function SLApprovalQueue() {
  const [detail, setDetail] = useState(null)
  const { data, isLoading, isError, error } = useListProposalsQuery({ page: 0, size: 100 })

  const rows = useMemo(() => (Array.isArray(data) ? data.map(proposalRow) : []), [data])
  const awaiting = useMemo(() => rows
    .filter((r) => inGroup('awaiting', r.apiStatus))
    .sort((a, b) => new Date(b.raw?.updatedAt || 0) - new Date(a.raw?.updatedAt || 0)), [rows]) // most recently changed first
  // Recently sent — everything sent onward, latest activity first (capped at 15 rows).
  const recent = useMemo(() => rows
    .filter((r) => inGroup('recent', r.apiStatus))
    .sort((a, b) => new Date(b.raw?.updatedAt || 0) - new Date(a.raw?.updatedAt || 0))
    .slice(0, 15), [rows])
  const kpis = useMemo(() => computeKpis(data, 'sl-queue'), [data])

  const awaitingCols = [
    { key: 'brand', label: 'Brand', render: (r) => <b>{r.brand}</b> },
    { key: 'hubspot', label: 'HubSpot' },
    { key: 'card', label: 'Card' },
    { key: 'with', label: 'With' },
    { key: 'status', label: 'Status', render: (r) => <Pill label={r.status} styleKey={r.statusKey} /> },
    { key: 'waiting', label: 'Waiting', align: 'right' },
    { key: 'a', label: 'Action', align: 'right', render: (r) => <ActionIconBtn title="View" onClick={() => setDetail(r)}><VisibilityOutlinedIcon fontSize="small" /></ActionIconBtn> },
  ]
  const recentCols = [
    { key: 'brand', label: 'Brand', render: (r) => <b>{r.brand}</b> },
    { key: 'card', label: 'Card' },
    { key: 'status', label: 'Status', render: (r) => <Pill label={r.status} styleKey={r.statusKey} /> },
    { key: 'sent', label: 'Updated' },
    { key: 'a', label: 'Action', align: 'right', render: (r) => <ActionIconBtn title="Open" onClick={() => setDetail(r)}><VisibilityOutlinedIcon fontSize="small" /></ActionIconBtn> },
  ]

  return (
    <Box sx={{ px: 3 }}>
      <PageTitle>Approval Queue</PageTitle>
      <KpiRow items={kpis} />
      {isLoading && <LinearProgress sx={{ my: 1 }} />}
      {isError && <Alert severity="error" sx={{ my: 1 }}>{error?.message || 'Failed to load proposals.'}</Alert>}
      <SectionCard title="Awaiting a decision" subtitle="Proposals you submitted that are with Pricing or the Functional Head. You are notified the moment a decision is made.">
        <DataTable columns={awaitingCols} rows={awaiting} onRowClick={(r) => setDetail(r)} getRowKey={(r) => r.id} emptyText={isLoading ? 'Loading…' : 'Nothing awaiting a decision'} />
      </SectionCard>
      <SectionCard title="Recently sent" subtitle="Everything you've sent onward — latest activity first, including approvals, returns and closed deals.">
        <DataTable columns={recentCols} rows={recent} onRowClick={(r) => setDetail(r)} getRowKey={(r) => r.id} emptyText={isLoading ? 'Loading…' : 'Nothing sent yet'} />
      </SectionCard>
      <ProposalDetailModal open={!!detail} proposal={detail} onClose={() => setDetail(null)} />
    </Box>
  )
}
